import React, { useRef, useEffect, useLayoutEffect, useState, useCallback, useMemo } from "react";
import MessageLayout from "../messageLayout";
import type { MessageListProps, MessageData } from "@/types/chat.types";
import { useTranslation } from "react-i18next";

/**
 * Format date header - WhatsApp/Telegram style
 */
function formatDateHeader(timestamp: string | number | Date | null | undefined): string {
  if (!timestamp) return "";

  const date = new Date(typeof timestamp === 'number' && timestamp < 10000000000 ? timestamp * 1000 : timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Reset time to midnight for accurate date comparison
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

  if (dateOnly.getTime() === todayOnly.getTime()) {
    return "Today";
  } else if (dateOnly.getTime() === yesterdayOnly.getTime()) {
    return "Yesterday";
  } else {
    // Show full date for older messages
    return date.toLocaleDateString([], {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  }
}

/**
 * Get date key for grouping messages
 */
function getDateKey(timestamp: string | number | Date | null | undefined): string {
  if (!timestamp) return "";
  const date = new Date(typeof timestamp === 'number' && timestamp < 10000000000 ? timestamp * 1000 : timestamp);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

/**
 * MessageList Component
 *
 * Responsibilities:
 * - Display list of messages
 * - Handle scrolling and scroll-to-bottom logic
 * - Manage pagination/infinite scroll
 * - Pass message-specific handlers to MessageLayout
 * - Highlight messages when navigating
 * - Track visible messages using Intersection Observer
 *
 * Parent: ChatArea
 * Children: MessageLayout (one per message)
 */
const MessageList: React.FC<MessageListProps> = ({
  messages,
  currentUser,
  isLoading = false,
  onLoadMore,
  onLoadNewer,
  hasMore = false,
  hasNewer = false,
  eventHandlers,
  repliedMessage,
  currentRoom,
  onScrollInfo,
  firstUnreadMessageId,
  onSaveScrollPosition,
  savedScrollPosition,
  onClearUnreadDivider,
  onDividerVisible,
  onDividerHidden,
  newMessagesCount: externalNewMessagesCount,
  isAutoLoading = false,
  isLoadingOlder: isLoadingOlderProp = false,
  isLoadingNewer: isLoadingNewerProp = false,
}) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const prevMessageCountRef = useRef(messages.length);
  const prevScrollHeightRef = useRef(0);
  const [visibleMessageIds, setVisibleMessageIds] = useState<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Scroll direction tracking
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down' | 'none'>('none');
  const lastScrollTopRef = useRef<number>(0);

  // Track if initial scroll to unread has been done
  const initialScrollDoneRef = useRef<boolean>(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastRoomKeyRef = useRef<string | null>(null);

  // CRITICAL: Content visibility control
  // Content is HIDDEN until scroll position is set to prevent "jump" effect
  const [isContentVisible, setIsContentVisible] = useState(false);
  const visibilityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // New messages floating button state (Telegram-style)
  // Use external count from useUnreadDivider hook if provided, otherwise use internal state
  const [internalNewMessagesCount, setInternalNewMessagesCount] = useState(0);
  const newMessagesCount = externalNewMessagesCount ?? internalNewMessagesCount;
  const setNewMessagesCount = setInternalNewMessagesCount;
  const newMessagesBaseRef = useRef<number>(0); // Base message count when user scrolled up

  // Sticky date indicator state (shows current date while scrolling)
  const [stickyDate, setStickyDate] = useState<string | null>(null);
  const stickyDateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track if unread divider should be shown (hide after user scrolls past it)
  const [showUnreadDivider, setShowUnreadDivider] = useState(true);
  const unreadDividerClearedRef = useRef(false);

  // Track divider visibility for 1 second rule
  const dividerObserverRef = useRef<IntersectionObserver | null>(null);
  const dividerVisibleRef = useRef<boolean>(false);

  // Track if user WAS at bottom before new message arrived (for auto-scroll)
  const wasAtBottomRef = useRef<boolean>(true);

  // Track last message ID to detect new messages (length alone is not reliable)
  const lastMessageIdRef = useRef<string | null>(null);

  // Track previous last message ID for floating button (declared here, used in effect below)
  const prevLastMessageIdForButtonRef = useRef<string | null>(null);

  // Track first message ID to detect when older messages are prepended
  const prevFirstMessageIdRef = useRef<string | null>(null);

  // Track previous firstUnreadMessageId to detect when it changes from null to a value
  const prevFirstUnreadIdRef = useRef<string | null | undefined>(undefined);

  // Flag to indicate we're expecting a prepend (older messages loading)
  // Keep refs for scroll position preservation logic
  const isLoadingOlderRef = useRef<boolean>(false);

  // Flag to indicate we're loading newer messages via pagination
  const isLoadingNewerRef = useRef<boolean>(false);

  // Use props from parent (single source of truth) instead of local state
  const isLoadingOlder = isLoadingOlderProp;
  const isLoadingNewer = isLoadingNewerProp;

  // Sync refs with props for scroll position preservation logic
  useEffect(() => {
    if (isLoadingOlderProp) {
      isLoadingOlderRef.current = true;
    }
  }, [isLoadingOlderProp]);

  useEffect(() => {
    if (isLoadingNewerProp) {
      isLoadingNewerRef.current = true;
    }
  }, [isLoadingNewerProp]);

  // Store the element to anchor scroll position to
  const scrollAnchorIdRef = useRef<string | null>(null);
  const scrollAnchorOffsetRef = useRef<number>(0);

  // Save scroll position when leaving a room
  const prevRoomKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const prevKey = prevRoomKeyRef.current;
    const currentKey = currentRoom?.session_key || null;

    // If we're leaving a room (key changed and we had a previous room)
    if (prevKey && prevKey !== currentKey && messagesContainerRef.current && onSaveScrollPosition) {
      onSaveScrollPosition(messagesContainerRef.current, messages);
    }

    prevRoomKeyRef.current = currentKey;
  }, [currentRoom?.session_key, messages, onSaveScrollPosition]);

  // Reset state when room changes
  useEffect(() => {
    if (currentRoom?.session_key !== lastRoomKeyRef.current) {
      setVisibleMessageIds(new Set());
      initialScrollDoneRef.current = false;
      lastRoomKeyRef.current = currentRoom?.session_key || null;
      // Don't set shouldScrollToBottom to true - wait for initial scroll logic
      setShouldScrollToBottom(false);
      wasAtBottomRef.current = false;
      lastMessageIdRef.current = null;
      prevLastMessageIdForButtonRef.current = null;
      prevFirstMessageIdRef.current = null;
      isLoadingOlderRef.current = false;
      isLoadingNewerRef.current = false;
      // Note: loading state is now controlled by parent via props
      scrollAnchorIdRef.current = null;
      scrollAnchorOffsetRef.current = 0;
      // Reset new messages count
      setNewMessagesCount(0);
      newMessagesBaseRef.current = 0;
      // Reset sticky date
      setStickyDate(null);
      // Reset unread divider
      setShowUnreadDivider(true);
      unreadDividerClearedRef.current = false;
      prevFirstUnreadIdRef.current = undefined; // Reset for new room
      // CRITICAL: Hide content until scroll is set for new room
      setIsContentVisible(false);
      // Clear visibility timeout
      if (visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current);
        visibilityTimeoutRef.current = null;
      }
      // Clear any pending scroll timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
      if (stickyDateTimeoutRef.current) {
        clearTimeout(stickyDateTimeoutRef.current);
        stickyDateTimeoutRef.current = null;
      }
    }
  }, [currentRoom?.session_key]);

  // CRITICAL: Reset initialScrollDone when firstUnreadMessageId changes from null to a valid value
  // This handles the case where API returns null first, then returns the actual unread ID
  useEffect(() => {
    const prevId = prevFirstUnreadIdRef.current;
    const currentId = firstUnreadMessageId;

    // If we previously had null (or undefined) and now we have a valid ID
    // AND we already scrolled to bottom (initialScrollDone is true)
    // Then we need to re-scroll to the divider
    if ((prevId === null || prevId === undefined) && currentId && initialScrollDoneRef.current) {
      console.log(`🔄 [DIVIDER DEBUG] firstUnreadId changed from ${prevId} to ${currentId}, resetting initialScrollDone`);
      initialScrollDoneRef.current = false;
      setShowUnreadDivider(true);
      unreadDividerClearedRef.current = false;
    }

    prevFirstUnreadIdRef.current = currentId;
  }, [firstUnreadMessageId]);

  const { t } = useTranslation("chat");

  // Get the first and last message IDs for comparison
  const currentFirstMessageId = messages.length > 0 ? messages[0]?.messageId : null;
  const currentLastMessageId = messages.length > 0 ? messages[messages.length - 1]?.messageId : null;

  /**
   * SYNCHRONOUS scroll position restoration when older messages are prepended
   * useLayoutEffect runs synchronously BEFORE browser paint
   */
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || messages.length === 0) return;

    // Check if older messages were prepended (first message ID changed, but last message ID stayed same)
    const prevFirstId = prevFirstMessageIdRef.current;
    const prevLastId = lastMessageIdRef.current;
    const firstIdChanged = prevFirstId && currentFirstMessageId !== prevFirstId;
    const lastIdSame = prevLastId && currentLastMessageId === prevLastId;

    // Older messages prepended: first ID changed but last ID is same (user-triggered pagination)
    // OR: first ID changed and initial scroll not done yet (auto-load case)
    const isUserPagination = firstIdChanged && lastIdSame && scrollAnchorIdRef.current;
    const isAutoLoadPrepend = firstIdChanged && !initialScrollDoneRef.current && firstUnreadMessageId;

    if (isUserPagination) {
      // User-triggered pagination: restore scroll position to anchor
      const anchorElement = document.getElementById(`message-${scrollAnchorIdRef.current}`);
      if (anchorElement) {
        // Get the anchor's new position and restore scroll
        const anchorRect = anchorElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const currentOffset = anchorRect.top - containerRect.top;
        const scrollAdjustment = currentOffset - scrollAnchorOffsetRef.current;

        container.scrollTop = container.scrollTop + scrollAdjustment;
      }

      // Clear anchor after restoration
      scrollAnchorIdRef.current = null;
      scrollAnchorOffsetRef.current = 0;
      isLoadingOlderRef.current = false;
      // Note: loading state is now controlled by parent via props
    } else if (isAutoLoadPrepend) {
      // Auto-load completed: Scroll to first unread message (divider)
      // This runs AFTER older messages are prepended
      console.log(`🔄 [DIVIDER DEBUG] Auto-load prepend detected, scrolling to divider`);
      const dividerElement = document.getElementById('unread-divider');
      const unreadElement = document.getElementById(`message-${firstUnreadMessageId}`);

      if (dividerElement) {
        dividerElement.scrollIntoView({ behavior: "auto", block: "start" });
        initialScrollDoneRef.current = true;
        setShouldScrollToBottom(false);
        wasAtBottomRef.current = false;
        console.log(`✅ [DIVIDER DEBUG] Scrolled to DIVIDER after auto-load`);
      } else if (unreadElement) {
        unreadElement.scrollIntoView({ behavior: "auto", block: "start" });
        initialScrollDoneRef.current = true;
        setShouldScrollToBottom(false);
        wasAtBottomRef.current = false;
        console.log(`✅ [DIVIDER DEBUG] Scrolled to MESSAGE after auto-load`);
      }
    }

    // Update tracking refs
    prevFirstMessageIdRef.current = currentFirstMessageId;
  }, [messages, currentFirstMessageId, currentLastMessageId, firstUnreadMessageId]);

  /**
   * CRITICAL: Initial scroll positioning using useLayoutEffect
   *
   * This runs BEFORE browser paint, ensuring:
   * 1. No unread: User sees bottom position immediately (no jump)
   * 2. With unread: User sees divider position immediately
   *
   * Content visibility is controlled separately - content is hidden until scroll is set.
   */
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const currentCount = messages.length;

    // Only proceed when:
    // 1. Initial scroll not done yet
    // 2. We have messages
    // 3. firstUnreadMessageId is NOT undefined (meaning data is ready)
    // 4. NOT auto-loading (wait for auto-load to complete)
    if (!initialScrollDoneRef.current && currentCount > 0 && firstUnreadMessageId !== undefined && !isAutoLoading) {
      console.log(`🎯 [LAYOUT] Initial scroll: firstUnreadId=${firstUnreadMessageId}, msgCount=${currentCount}`);

      if (firstUnreadMessageId) {
        // Has unread messages - scroll to divider or message
        const dividerElement = document.getElementById('unread-divider');
        const unreadElement = document.getElementById(`message-${firstUnreadMessageId}`);

        if (dividerElement) {
          dividerElement.scrollIntoView({ behavior: "auto", block: "start" });
          console.log(`✅ [LAYOUT] Scrolled to DIVIDER`);
        } else if (unreadElement) {
          unreadElement.scrollIntoView({ behavior: "auto", block: "start" });
          console.log(`✅ [LAYOUT] Scrolled to MESSAGE`);
        } else {
          // Element not found yet - will retry in useEffect
          console.log(`⏳ [LAYOUT] Element not found, will retry`);
          return; // Don't mark as done yet
        }

        initialScrollDoneRef.current = true;
        setShouldScrollToBottom(false);
        wasAtBottomRef.current = false;
        // Make content visible AFTER scroll is set
        setIsContentVisible(true);
      } else {
        // No unread messages - scroll to bottom IMMEDIATELY
        container.scrollTop = container.scrollHeight;
        console.log(`✅ [LAYOUT] Scrolled to BOTTOM (no unread)`);

        initialScrollDoneRef.current = true;
        setShouldScrollToBottom(true);
        wasAtBottomRef.current = true;
        // Make content visible AFTER scroll is set
        setIsContentVisible(true);
      }

      lastMessageIdRef.current = currentLastMessageId;
      prevMessageCountRef.current = currentCount;
    }

    // FALLBACK: Ensure content becomes visible even if scroll logic fails
    // This prevents content from being permanently hidden
    if (!isContentVisible && messages.length > 0 && firstUnreadMessageId !== undefined && !isAutoLoading) {
      if (visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current);
      }
      visibilityTimeoutRef.current = setTimeout(() => {
        if (!isContentVisible) {
          console.log(`⚠️ [VISIBILITY] Fallback: Making content visible after timeout`);
          setIsContentVisible(true);
        }
      }, 300); // 300ms fallback - should be enough for DOM to render
    }
  }, [messages, firstUnreadMessageId, isAutoLoading, currentLastMessageId, isContentVisible]);

  /**
   * Fallback scroll with retry (for cases where DOM isn't ready in useLayoutEffect)
   */
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const currentCount = messages.length;

    // Only run if useLayoutEffect didn't complete (element wasn't found)
    if (!initialScrollDoneRef.current && currentCount > 0 && firstUnreadMessageId !== undefined && !isAutoLoading) {
      // Clear any pending timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      const targetMessageId = firstUnreadMessageId;

      const attemptScroll = (retryCount: number = 0) => {
        scrollTimeoutRef.current = null;
        if (!messagesContainerRef.current) return;
        if (initialScrollDoneRef.current) return;

        if (targetMessageId) {
          const dividerElement = document.getElementById('unread-divider');
          const unreadElement = document.getElementById(`message-${targetMessageId}`);

          if (dividerElement) {
            dividerElement.scrollIntoView({ behavior: "auto", block: "start" });
            initialScrollDoneRef.current = true;
            setShouldScrollToBottom(false);
            wasAtBottomRef.current = false;
            setIsContentVisible(true);
            console.log(`✅ [RETRY] Scrolled to DIVIDER`);
            return;
          } else if (unreadElement) {
            unreadElement.scrollIntoView({ behavior: "auto", block: "start" });
            initialScrollDoneRef.current = true;
            setShouldScrollToBottom(false);
            wasAtBottomRef.current = false;
            setIsContentVisible(true);
            console.log(`✅ [RETRY] Scrolled to MESSAGE`);
            return;
          }

          // Retry with limit
          const MAX_RETRIES = 5;
          if (retryCount < MAX_RETRIES) {
            scrollTimeoutRef.current = setTimeout(() => attemptScroll(retryCount + 1), 50);
            return;
          }

          // Give up - scroll to top and show content
          messagesContainerRef.current.scrollTop = 0;
          initialScrollDoneRef.current = true;
          setIsContentVisible(true);
          console.log(`⚠️ [RETRY] Element not found, scrolled to top`);
        }
      };

      // Start retry after small delay
      scrollTimeoutRef.current = setTimeout(() => attemptScroll(0), 20);
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [messages, firstUnreadMessageId, isAutoLoading]);

  /**
   * Handle new message auto-scroll (real-time WebSocket messages)
   */
  useEffect(() => {
    // Check if new message arrived by comparing last message ID
    const hasNewMessage = currentLastMessageId && currentLastMessageId !== lastMessageIdRef.current;

    // New message arrived (at the bottom of the list) - NOT a prepend or pagination
    if (hasNewMessage && !isLoadingOlderRef.current && !isLoadingNewerRef.current) {
      const wasAtBottom = wasAtBottomRef.current;

      if (wasAtBottom) {
        // User was at bottom - auto-scroll to show new message
        requestAnimationFrame(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            wasAtBottomRef.current = true;
            setShouldScrollToBottom(true);
          }
        });
      }
    }

    // Reset pagination flags after processing
    if (isLoadingNewerRef.current) {
      isLoadingNewerRef.current = false;
    }

    lastMessageIdRef.current = currentLastMessageId;
    prevMessageCountRef.current = messages.length;
  }, [messages, currentLastMessageId]);

  /**
   * Setup Intersection Observer to track visible messages
   * With minimum visibility duration of 300ms (per CHAT_SCROLL_PROMPT.md requirements)
   */
  // Track pending visibility timers for minimum duration requirement
  const visibilityTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const container = messagesContainerRef.current;
    if (!container) return;

    const MINIMUM_VISIBILITY_DURATION = 300; // ms - minimum time message must be visible

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const messageId = entry.target.getAttribute('data-message-id');
          if (!messageId) return;

          // Only count as visible if at least 50% is showing
          const isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.5;

          if (isVisible) {
            // Start a timer if not already running for this message
            if (!visibilityTimersRef.current.has(messageId)) {
              const timer = setTimeout(() => {
                // Message has been visible for minimum duration, add to visible set
                setVisibleMessageIds((prev) => {
                  const updated = new Set(prev);
                  updated.add(messageId);
                  return updated;
                });
                visibilityTimersRef.current.delete(messageId);
              }, MINIMUM_VISIBILITY_DURATION);

              visibilityTimersRef.current.set(messageId, timer);
            }
          } else {
            // Message is no longer visible - cancel pending timer and remove from visible set
            const pendingTimer = visibilityTimersRef.current.get(messageId);
            if (pendingTimer) {
              clearTimeout(pendingTimer);
              visibilityTimersRef.current.delete(messageId);
            }

            setVisibleMessageIds((prev) => {
              const updated = new Set(prev);
              updated.delete(messageId);
              return updated;
            });
          }
        });
      },
      {
        root: container,
        threshold: [0, 0.5, 1], // Track visibility at 0%, 50%, and 100%
        rootMargin: "0px", // No margin - exact viewport
      }
    );

    // Helper function to check if element is at least 50% visible in viewport
    const isElementSufficientlyVisible = (el: Element, containerEl: Element) => {
      const rect = el.getBoundingClientRect();
      const containerRect = containerEl.getBoundingClientRect();

      // Calculate how much of the element is visible
      const visibleTop = Math.max(rect.top, containerRect.top);
      const visibleBottom = Math.min(rect.bottom, containerRect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const elementHeight = rect.height;

      // Return true only if at least 50% is visible
      return elementHeight > 0 && (visibleHeight / elementHeight) >= 0.5;
    };

    const timer = setTimeout(() => {
      const messageElements = container.querySelectorAll('[data-message-id]');
      const initialVisible = new Set<string>();

      messageElements.forEach((el) => {
        observerRef.current?.observe(el);

        // Check if already 50%+ visible and add to initial set
        const messageId = el.getAttribute('data-message-id');
        if (messageId && isElementSufficientlyVisible(el, container)) {
          initialVisible.add(messageId);
        }
      });

      // Set initial visible messages
      if (initialVisible.size > 0) {
        setVisibleMessageIds(initialVisible);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      observerRef.current?.disconnect();
      // Clear all pending visibility timers
      visibilityTimersRef.current.forEach((timerRef) => clearTimeout(timerRef));
      visibilityTimersRef.current.clear();
    };
  }, [messages, currentRoom?.session_key]); // currentRoom değiştiğinde de observer kurulsun


  /**
   * Calculate the most recent visible message (highest array index)
   */
  const getMaxVisibleMessageId = useCallback((): string | null => {
    if (visibleMessageIds.size === 0) return null;
    let maxIndex = -1;
    let maxId: string | null = null;
    messages.forEach((msg, index) => {
      if (visibleMessageIds.has(msg.messageId) && index > maxIndex) {
        maxIndex = index;
        maxId = msg.messageId;
      }
    });
    return maxId;
  }, [messages, visibleMessageIds]);

  /**
   * Notify parent component of scroll state changes
   */
  useEffect(() => {
    if (onScrollInfo) {
      onScrollInfo({
        maxVisibleMessageId: getMaxVisibleMessageId(),
        isScrollingDown: scrollDirection === 'down',
        isAtBottom: shouldScrollToBottom,
        isInitialLoad: !initialScrollDoneRef.current
      });
    }
  }, [visibleMessageIds, scrollDirection, shouldScrollToBottom, onScrollInfo, getMaxVisibleMessageId]);

  // Helper function to check if element is at least 50% visible in viewport
  const isElementSufficientlyVisibleCb = useCallback((el: Element, containerEl: Element) => {
    const rect = el.getBoundingClientRect();
    const containerRect = containerEl.getBoundingClientRect();

    // Calculate how much of the element is visible
    const visibleTop = Math.max(rect.top, containerRect.top);
    const visibleBottom = Math.min(rect.bottom, containerRect.bottom);
    const visibleHeight = Math.max(0, visibleBottom - visibleTop);
    const elementHeight = rect.height;

    // Return true only if at least 50% is visible
    return elementHeight > 0 && (visibleHeight / elementHeight) >= 0.5;
  }, []);

  // Debounce ref for scroll-based visibility recalculation
  const scrollDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Recalculate visible messages on scroll (debounced)
  const recalculateVisibleMessages = useCallback(() => {
    // Clear previous timeout
    if (scrollDebounceRef.current) {
      clearTimeout(scrollDebounceRef.current);
    }

    // Debounce the recalculation
    scrollDebounceRef.current = setTimeout(() => {
      const container = messagesContainerRef.current;
      if (!container) return;

      const messageElements = container.querySelectorAll('[data-message-id]');
      const currentlyVisible = new Set<string>();

      messageElements.forEach((el) => {
        const messageId = el.getAttribute('data-message-id');
        if (messageId && isElementSufficientlyVisibleCb(el, container)) {
          currentlyVisible.add(messageId);
        }
      });

      setVisibleMessageIds(currentlyVisible);
    }, 150); // 150ms debounce
  }, [isElementSufficientlyVisibleCb]);

  /**
   * Scroll to the unread divider if it exists, otherwise scroll to bottom
   * This is what the "X new messages" button calls
   */
  const scrollToNewMessages = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // If there's an unread divider, scroll to it
    if (firstUnreadMessageId) {
      // First try to find the actual divider element
      const dividerElement = document.getElementById('unread-divider');
      const messageElement = document.getElementById(`message-${firstUnreadMessageId}`);

      if (dividerElement) {
        // Scroll to the divider element with block: "start" so it's at the top
        dividerElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      } else if (messageElement) {
        // Fallback: scroll to the message
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }

    // No divider or divider not found - scroll to bottom
    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth'
    });
    // Reset new messages count when scrolling to bottom
    setNewMessagesCount(0);
    newMessagesBaseRef.current = messages.length;
  }, [firstUnreadMessageId, messages.length]);

  /**
   * Scroll to bottom programmatically (always goes to bottom)
   */
  const scrollToBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
      // Reset new messages count when scrolling to bottom
      setNewMessagesCount(0);
      newMessagesBaseRef.current = messages.length;
    }
  }, [messages.length]);

  /**
   * Scroll to top (first message)
   */
  const scrollToTop = useCallback(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, []);

  /**
   * Update sticky date indicator based on visible messages
   */
  const updateStickyDate = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container || messages.length === 0) return;

    // Find the first visible message
    const containerRect = container.getBoundingClientRect();
    const messageElements = Array.from(container.querySelectorAll('[data-message-id]'));

    for (let i = 0; i < messageElements.length; i++) {
      const element = messageElements[i];
      const rect = element.getBoundingClientRect();
      // Check if message is at or near the top of viewport
      if (rect.top >= containerRect.top - 50 && rect.top <= containerRect.top + 100) {
        const messageId = element.getAttribute('data-message-id');
        const message = messages.find(m => m.messageId === messageId);
        if (message) {
          const timestamp = message.sentTime || message.arrivalTime;
          const dateStr = formatDateHeader(timestamp);
          if (dateStr && dateStr !== stickyDate) {
            setStickyDate(dateStr);
          }
        }
        break;
      }
    }

    // Clear sticky date after 1.5 seconds of no scrolling
    if (stickyDateTimeoutRef.current) {
      clearTimeout(stickyDateTimeoutRef.current);
    }
    stickyDateTimeoutRef.current = setTimeout(() => {
      setStickyDate(null);
    }, 1500);
  }, [messages, stickyDate]);

  /**
   * Keyboard navigation support
   */
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if container or its children have focus
      if (!container.contains(document.activeElement) && document.activeElement !== container) {
        return;
      }

      const scrollAmount = container.clientHeight * 0.8; // 80% of viewport

      switch (e.key) {
        case 'PageUp':
          e.preventDefault();
          container.scrollBy({ top: -scrollAmount, behavior: 'smooth' });
          break;
        case 'PageDown':
          e.preventDefault();
          container.scrollBy({ top: scrollAmount, behavior: 'smooth' });
          break;
        case 'Home':
          e.preventDefault();
          scrollToTop();
          break;
        case 'End':
          e.preventDefault();
          scrollToBottom();
          break;
        case 'Escape':
          // Scroll to new messages button or bottom
          if (newMessagesCount > 0) {
            e.preventDefault();
            scrollToBottom();
          }
          break;
      }
    };

    // Make container focusable
    container.setAttribute('tabindex', '0');
    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [scrollToBottom, scrollToTop, newMessagesCount]);

  /**
   * Track divider visibility for 1-second rule
   * When divider is visible for 1 second, notify parent to clear it
   */
  useEffect(() => {
    // Cleanup previous observer
    if (dividerObserverRef.current) {
      dividerObserverRef.current.disconnect();
    }

    // Only observe if there's a divider
    if (!firstUnreadMessageId) {
      dividerVisibleRef.current = false;
      return;
    }

    const container = messagesContainerRef.current;
    if (!container) return;

    // Wait a bit for DOM to render
    const setupTimer = setTimeout(() => {
      const dividerElement = document.getElementById('unread-divider');
      if (!dividerElement) return;

      dividerObserverRef.current = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            // Divider is visible
            if (!dividerVisibleRef.current) {
              dividerVisibleRef.current = true;
              onDividerVisible?.();
            }
          } else {
            // Divider is hidden
            if (dividerVisibleRef.current) {
              dividerVisibleRef.current = false;
              onDividerHidden?.();
            }
          }
        },
        {
          root: container,
          threshold: [0, 0.5, 1],
          rootMargin: '0px'
        }
      );

      dividerObserverRef.current.observe(dividerElement);
    }, 100);

    return () => {
      clearTimeout(setupTimer);
      dividerObserverRef.current?.disconnect();
    };
  }, [firstUnreadMessageId, onDividerVisible, onDividerHidden]);

  /**
   * Track if user is near bottom to auto-scroll new messages
   */
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const isNearBottom = distanceFromBottom < 100;

    // Update ref IMMEDIATELY so it's available for auto-scroll before state updates
    wasAtBottomRef.current = isNearBottom;

    // Reset new messages count when user scrolls to bottom
    if (isNearBottom && !shouldScrollToBottom) {
      setNewMessagesCount(0);
      newMessagesBaseRef.current = messages.length;
    }

    setShouldScrollToBottom(isNearBottom);

    // Track scroll direction
    const newDirection = scrollTop > lastScrollTopRef.current ? 'down' :
                         scrollTop < lastScrollTopRef.current ? 'up' : 'none';
    setScrollDirection(newDirection);
    lastScrollTopRef.current = scrollTop;

    // Load older messages when scrolling to top
    if (scrollTop < 50) {
      console.log(`📜 [SCROLL TOP] scrollTop=${scrollTop}, hasMore=${hasMore}, isLoading=${isLoading}, onLoadMore=${!!onLoadMore}`);
    }
    if (scrollTop < 50 && hasMore && onLoadMore && !isLoading) {
      // Save the first visible message as anchor for scroll restoration
      // Find the first message element that's visible in the viewport
      const firstVisibleMessageId = messages[0]?.messageId;
      if (firstVisibleMessageId) {
        const anchorElement = document.getElementById(`message-${firstVisibleMessageId}`);
        if (anchorElement) {
          const anchorRect = anchorElement.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          scrollAnchorIdRef.current = firstVisibleMessageId;
          scrollAnchorOffsetRef.current = anchorRect.top - containerRect.top;
          isLoadingOlderRef.current = true;
          // Note: loading state is now controlled by parent via props
        }
      }

      prevScrollHeightRef.current = scrollHeight;
      onLoadMore();
    }

    // Load newer messages when scrolling to bottom
    if (isNearBottom && hasNewer && onLoadNewer && !isLoading) {
      isLoadingNewerRef.current = true;
      // Note: loading state is now controlled by parent via props
      onLoadNewer();
    }

    // Update sticky date indicator while scrolling
    updateStickyDate();

    // NOTE: Divider is NO LONGER cleared when user scrolls past it.
    // Divider stays visible until user leaves the room (room change).
    // This matches Telegram behavior where "New Messages" divider persists.

    // Recalculate visible messages on scroll
    recalculateVisibleMessages();
  }, [hasMore, hasNewer, onLoadMore, onLoadNewer, isLoading, recalculateVisibleMessages, shouldScrollToBottom, messages, updateStickyDate]);

  /**
   * Track new messages arriving while user is scrolled up (Telegram-style floating button)
   */
  useEffect(() => {
    // Skip during initial load
    if (!initialScrollDoneRef.current) return;

    // Check if new message arrived (by ID comparison, not length)
    const hasNewMessage = currentLastMessageId && currentLastMessageId !== prevLastMessageIdForButtonRef.current;

    // When not at bottom and new message arrived, increment the counter
    if (hasNewMessage && !wasAtBottomRef.current) {
      setNewMessagesCount(prev => prev + 1);
    }

    prevLastMessageIdForButtonRef.current = currentLastMessageId;
  }, [currentLastMessageId]);

  /**
   * Navigate to a specific message and highlight it
   */
  const handleNavigateToMessage = (messageId: string) => {
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedMessageId(messageId);

      // Remove highlight after 3 seconds
      setTimeout(() => {
        setHighlightedMessageId(null);
      }, 3000);
    }
  };

  /**
   * Enhanced event handlers with navigation support
   */
  const enhancedHandlers = {
    ...eventHandlers,
    onNavigateToMessage: handleNavigateToMessage,
  };

  // Precompute date keys for messages to show date headers
  const messagesWithDateInfo = useMemo(() => {
    let prevDateKey: string | null = null;
    let dividerFound = false;

    const result = messages.map((message, index) => {
      const timestamp = message.sentTime || message.arrivalTime;
      const dateKey = getDateKey(timestamp);
      const showDateHeader = dateKey !== prevDateKey;
      prevDateKey = dateKey;
      const isFirstUnread = firstUnreadMessageId === message.messageId && showUnreadDivider;

      if (isFirstUnread) {
        dividerFound = true;
        console.log(`📍 [DIVIDER DEBUG] Divider MATCH at index ${index}, messageId="${message.messageId}"`);
      }

      return {
        message,
        showDateHeader,
        dateKey,
        dateHeader: showDateHeader ? formatDateHeader(timestamp) : null,
        isFirstUnread,
      };
    });

    // Log divider status
    console.log(`📊 [DIVIDER DEBUG] firstUnreadId="${firstUnreadMessageId}", showDivider=${showUnreadDivider}, msgCount=${messages.length}, found=${dividerFound}`);
    if (firstUnreadMessageId && !dividerFound && messages.length > 0) {
      console.log(`⚠️ [DIVIDER DEBUG] ID NOT IN LIST! First 3: [${messages.slice(0, 3).map(m => m.messageId).join(', ')}], Last 3: [${messages.slice(-3).map(m => m.messageId).join(', ')}]`);
    }

    return result;
  }, [messages, firstUnreadMessageId, showUnreadDivider]);

  return (
    <div className="relative h-full">
      {/* Sticky date indicator while scrolling */}
      {stickyDate && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 pointer-events-none animate-fade-in">
          <div className="px-3 py-1.5 bg-gray-800/80 dark:bg-gray-700/90 text-white text-xs font-medium rounded-full shadow-lg backdrop-blur-sm">
            {stickyDate}
          </div>
        </div>
      )}

      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="h-full overflow-y-auto px-2 sm:px-4 md:px-6 py-2 sm:py-4 pb-4 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent focus:outline-none"
        style={{
          // CRITICAL: Hide content until scroll position is set to prevent "jump"
          // opacity: 0 keeps layout but hides visually, transition makes it smooth when revealed
          opacity: isContentVisible ? 1 : 0,
          transition: 'opacity 0.05s ease-in-out',
        }}
      >
      {/* Loading indicator at top for older messages */}
      {isLoadingOlder && (
        <div className="sticky top-0 z-10 flex justify-center items-center py-3 mb-2">
          <div className="flex items-center gap-2 px-4 py-2 bg-purple-100 dark:bg-purple-900/40 rounded-full shadow-md backdrop-blur-sm">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-600 border-t-transparent"></div>
            <span className="text-sm text-purple-700 dark:text-purple-300 font-medium">
              {t("message.loadingOlder")}
            </span>
          </div>
        </div>
      )}

      {/* Messages with date headers */}
      {messagesWithDateInfo.map(({ message, showDateHeader, dateHeader, isFirstUnread }) => (
        <div key={message.messageId}>
          {/* Date header - WhatsApp/Telegram style */}
          {showDateHeader && dateHeader && (
            <div className="flex justify-center items-center my-4">
              <div className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-full shadow-sm">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                  {dateHeader}
                </span>
              </div>
            </div>
          )}

          {/* Unread messages divider*/}
          {isFirstUnread && (
            <div className="flex items-center my-3 px-4" id="unread-divider">
              <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent via-purple-400/60 to-purple-400/60 dark:via-purple-500/50 dark:to-purple-500/50"></div>
              <div className="flex items-center gap-1.5 mx-3 px-3 py-1 rounded-full bg-purple-50 dark:bg-purple-900/30 border border-purple-200/50 dark:border-purple-700/30">
                <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse"></div>
                <span className="text-xs font-medium text-purple-600 dark:text-purple-400">
                  {t("message.newMessages")}
                </span>
              </div>
              <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent via-purple-400/60 to-purple-400/60 dark:via-purple-500/50 dark:to-purple-500/50"></div>
            </div>
          )}

          <div
            id={`message-${message.messageId}`}
            data-message-id={message.messageId}
          >
            <MessageLayout
              message={message}
              currentRoom={currentRoom}
              onEditMessage={enhancedHandlers.onEdit}
              handleDeleteMessage={(messageId: string, chatroomId: string) =>
                enhancedHandlers.onDelete(messageId, chatroomId)
              }
              handleUndoMessage={(messageId: string, toName: string) =>
                enhancedHandlers.onDelete(messageId, toName)
              }
              onReply={(msg: any) => enhancedHandlers.onReply(msg)}
              onNavigateToMessage={enhancedHandlers.onNavigateToMessage}
              isHighlighted={highlightedMessageId === message.messageId}
            />
          </div>
        </div>
      ))}

      {/* Loading indicator at bottom for newer messages */}
      {isLoadingNewer && (
        <div className="sticky bottom-0 z-10 flex justify-center items-center py-3 mt-2">
          <div className="flex items-center gap-2 px-4 py-2 bg-purple-100 dark:bg-purple-900/40 rounded-full shadow-md backdrop-blur-sm">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-600 border-t-transparent"></div>
            <span className="text-sm text-purple-700 dark:text-purple-300 font-medium">
              {t("message.loadingNewer")}
            </span>
          </div>
        </div>
      )}

      {/* Small spacer for visual breathing room */}
      {messages.length > 0 && <div className="h-4" aria-hidden="true" />}

      {/* Loading skeleton */}
      {messages.length === 0 && isLoading && (
        <div className="flex flex-col gap-4 p-4 animate-pulse">
          {/* Incoming message skeleton */}
          <div className="flex gap-3 max-w-[70%]">
            <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0" />
            <div className="flex flex-col gap-2">
              <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
              <div className="h-16 w-48 bg-gray-200 dark:bg-gray-700 rounded-lg" />
            </div>
          </div>
          {/* Outgoing message skeleton */}
          <div className="flex gap-3 max-w-[70%] self-end flex-row-reverse">
            <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0" />
            <div className="flex flex-col gap-2 items-end">
              <div className="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
              <div className="h-12 w-40 bg-purple-200 dark:bg-purple-900/30 rounded-lg" />
            </div>
          </div>
          {/* Another incoming message skeleton */}
          <div className="flex gap-3 max-w-[70%]">
            <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0" />
            <div className="flex flex-col gap-2">
              <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
              <div className="h-20 w-56 bg-gray-200 dark:bg-gray-700 rounded-lg" />
            </div>
          </div>
          {/* Another outgoing message skeleton */}
          <div className="flex gap-3 max-w-[70%] self-end flex-row-reverse">
            <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0" />
            <div className="flex flex-col gap-2 items-end">
              <div className="h-3 w-14 bg-gray-200 dark:bg-gray-700 rounded" />
              <div className="h-10 w-32 bg-purple-200 dark:bg-purple-900/30 rounded-lg" />
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {messages.length === 0 && !isLoading && (
        <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
          <p>{t("message.noMessages")}</p>
        </div>
      )}
      </div>

      {/* New messages floating button (Telegram-style) */}
      {/* Shows when: newMessagesCount > 0 AND user is not at bottom */}
      {newMessagesCount > 0 && !shouldScrollToBottom && (
        <button
          onClick={scrollToNewMessages}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-full shadow-lg transition-all duration-200 z-10"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
          <span className="text-sm font-medium">
            {newMessagesCount > 50 ? "50+" : newMessagesCount} {t("message.newMessagesButton")}
          </span>
        </button>
      )}
    </div>
  );
};

export default MessageList;
